import React, { useEffect, useState } from 'react';
import { Typography, Tag, Space, Button, message } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { usePostContext } from '../../App';
import './index.css';
import 'antd/dist/reset.css';

const { Title, Paragraph, Text } = Typography;

const PostDetail = () => {
  const { id } = useParams();
  const { 
    posts, 
    loggedInUser, 
    incrementPostViews, 
    togglePostLike, 
    isPostLikedByUser,
    hasUserViewedPost 
  } = usePostContext();
  const navigate = useNavigate();
  
  const post = posts.find(p => p.id === id);
  const [hasIncrementedView, setHasIncrementedView] = useState(false);

  // 页面加载时增加浏览量（仅当用户未浏览过且未增加过本次浏览）
  useEffect(() => {
    if (post && loggedInUser && !hasUserViewedPost(post.id) && !hasIncrementedView) {
      // 延迟增加浏览量，确保页面完全加载后再计数
      const timer = setTimeout(() => {
        incrementPostViews(post.id);
        setHasIncrementedView(true); // 标记已增加过本次浏览
      }, 1000); // 1秒后增加浏览量
      
      return () => clearTimeout(timer);
    }
  }, [post, loggedInUser, incrementPostViews, hasUserViewedPost, hasIncrementedView]);

  if (!post) {
    return (
      <div className="post-detail-container">
        <h1>文章不存在</h1>
        <Link to="/archive">
          <Button>返回文章列表</Button>
        </Link>
      </div>
    );
  }

  // 检查是否为当前用户的文章（用于显示编辑按钮）
  const isCurrentUserPost = post.author === loggedInUser?.username;

  const handleEdit = () => {
    navigate('/write', { state: { post } });
  };

  // 处理点赞
  const handleLike = () => {
    if (!loggedInUser) {
      message.warning('请先登录以点赞文章');
      return;
    }
    
    togglePostLike(post.id);
    
    const isLiked = isPostLikedByUser(post.id);
    message.success(isLiked ? '已点赞' : '已取消点赞');
  };

  const isLiked = isPostLikedByUser(post.id);

  return (
    <div className="post-detail-container">
      <div className="post-detail-header">
        <Title level={2}>{post.title}</Title>
        <div className="post-detail-meta">
          <Text type="secondary">
            发布时间：{post.publishTime} | 作者：{post.author}
          </Text>
          {post.category && (
            <Tag color="blue" className="post-category-tag">
              {post.category}
            </Tag>
          )}
        </div>
        <Space size="middle" className="post-detail-tags">
          {Array.isArray(post.tags) 
            ? post.tags.map(tag => <Tag key={tag}>{tag}</Tag>) 
            : post.tags ? <Tag>{post.tags}</Tag> : null
          }
        </Space>
      </div>
      
      <div className="post-detail-content">
        <Paragraph className="post-summary">
          <strong>摘要：</strong>{post.summary}
        </Paragraph>
        <div className="post-content">
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
            {post.content}
          </pre>
        </div>
      </div>
      
      <div className="post-detail-stats">
        <Space size="large">
          <span className="post-views">👁️ {post.views || 0} 次浏览</span>
          <span 
            className={`post-likes ${isLiked ? 'liked' : ''}`}
            onClick={handleLike}
            style={{ cursor: 'pointer' }}
          >
            {isLiked ? '❤️' : '🤍'} {post.likeCount || 0} 点赞
          </span>
        </Space>
      </div>
      
      <div className="post-detail-actions">
        <Link to="/archive">
          <Button>返回文章列表</Button>
        </Link>
        {isCurrentUserPost && (
          <Button type="primary" onClick={handleEdit} style={{ marginLeft: 8 }}>
            编辑文章
          </Button>
        )}
      </div>
    </div>
  );
};

export default PostDetail;